import React, { useCallback, useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDownIcon, CheckIcon, XIcon } from 'lucide-react';
interface SelectOption {
  value: string;
  label: string;
}
interface SelectProps {
  label?: string;
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  searchable?: boolean;
  clearable?: boolean;
  className?: string;
}
export function Select({
  label,
  options,
  value,
  onChange,
  placeholder = 'Seleccionar...',
  error,
  disabled = false,
  searchable = true,
  clearable = true,
  className = ''
}: SelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const selectedOption = options.find(opt => opt.value === value);
  const filteredOptions = searchTerm ? options.filter(opt => opt.label.toLowerCase().includes(searchTerm.toLowerCase())) : options;
  // Calculate dropdown position based on trigger element
  const updateDropdownPosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const dropdownHeight = 280; // approximate max height
    const openAbove = spaceBelow < dropdownHeight && rect.top > dropdownHeight;
    setDropdownStyle({
      position: 'fixed',
      left: rect.left,
      width: rect.width,
      zIndex: 9999,
      ...(openAbove ? {
        bottom: window.innerHeight - rect.top + 4
      } : {
        top: rect.bottom + 4
      })
    });
  }, []);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current && !containerRef.current.contains(target) && dropdownRef.current && !dropdownRef.current.contains(target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  useEffect(() => {
    if (isOpen && searchable && inputRef.current) {
      inputRef.current.focus();
    }
    if (isOpen) {
      updateDropdownPosition();
    }
  }, [isOpen, searchable, updateDropdownPosition]);
  // Reposition on scroll/resize while open
  useEffect(() => {
    if (!isOpen) return;
    const handleReposition = () => updateDropdownPosition();
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);
    return () => {
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [isOpen, updateDropdownPosition]);
  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm('');
  };
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setSearchTerm('');
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
    } else if (e.key === 'Enter' && filteredOptions.length > 0) {
      handleSelect(filteredOptions[0].value);
    }
  };
  const dropdown = isOpen && !disabled ? <div ref={dropdownRef} style={dropdownStyle} className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-xl max-h-60 overflow-hidden" data-id="element-4876">
        {searchable && <div className="p-2 border-b border-gray-200 dark:border-gray-700" data-id="element-4877">
            <input ref={inputRef} type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} onKeyDown={handleKeyDown} placeholder="Buscar..." className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-sky-500" data-id="element-4878" />
          </div>}
        <div className="overflow-y-auto max-h-48" data-id="element-4879">
          {filteredOptions.length === 0 ? <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400" data-id="element-4880">
              No se encontraron resultados
            </div> : filteredOptions.map(option => <div key={option.value} onClick={() => handleSelect(option.value)} className={`
                px-3 py-2 cursor-pointer flex items-center justify-between
                hover:bg-sky-50 dark:hover:bg-sky-900/20
                ${option.value === value ? 'bg-sky-50 dark:bg-sky-900/20 text-sky-600 dark:text-sky-400' : 'text-gray-900 dark:text-gray-100'}
              `} data-id="element-4881">
                <span data-id="element-4882">{option.label}</span>
                {option.value === value && <CheckIcon className="w-4 h-4 text-sky-500" data-id="element-4883" />}
              </div>)}
        </div>
      </div> : null;
  return <div className={`w-full ${className}`} ref={containerRef} data-id="element-4884">
      {label && <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4885">
          {label}
        </label>}
      <div className="relative" data-id="element-4886">
        <div ref={triggerRef} onClick={() => !disabled && setIsOpen(!isOpen)} className={`
            w-full px-3 py-2 rounded-lg border cursor-pointer
            bg-white dark:bg-gray-800
            text-gray-900 dark:text-gray-100
            transition-colors duration-150
            flex items-center justify-between
            ${disabled ? 'bg-gray-100 cursor-not-allowed dark:bg-gray-700' : ''}
            ${error ? 'border-red-500 focus:ring-red-500' : isOpen ? 'border-sky-500 ring-2 ring-sky-500' : 'border-gray-300 dark:border-gray-600'}
          `} data-id="element-4887">
          <span className={selectedOption ? '' : 'text-gray-400 dark:text-gray-500'} data-id="element-4888">
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <div className="flex items-center gap-1" data-id="element-4889">
            {clearable && value && !disabled && <button onClick={handleClear} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded" type="button" data-id="element-4890">
                <XIcon className="w-4 h-4 text-gray-400" data-id="element-4891" />
              </button>}
            <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-4892" />
          </div>
        </div>

        {/* Render dropdown in a portal so it escapes overflow containers */}
        {dropdown && createPortal(dropdown, document.body)}
      </div>
      {error && <p className="mt-1 text-sm text-red-500" data-id="element-4893">{error}</p>}
    </div>;
}